import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/common/PageHeader';
import { collection, query, onSnapshot, doc, setDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { CheckSquare, Square, Calendar as CalendarIcon, Clock, Users, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const SupervisorLog = () => {
    const { currentUser } = useAuth();
    const role = currentUser?.role?.toUpperCase() || '';
    const isAtencion = role === 'ATENCIÓN AL CLIENTE';
    const isCuentas = role === 'GESTIÓN DE CUENTAS';
    const isMarketing = role === 'MARKETING';
    
    // Default tab based on role
    const [activeTab, setActiveTab] = useState(isCuentas ? 'cuentas' : isMarketing ? 'marketing' : 'atencion');

    const [viewDate, setViewDate] = useState(new Date().toISOString().split('T')[0]);
    const isToday = viewDate === new Date().toISOString().split('T')[0];

    const [rutinasAtencion, setRutinasAtencion] = useState([]);
    const [rutinasCuentas, setRutinasCuentas] = useState([]);
    const [rutinasMarketing, setRutinasMarketing] = useState([]);
    const [logDia, setLogDia] = useState({});
    const [evidenceInputs, setEvidenceInputs] = useState({});

    useEffect(() => {
        const q = query(collection(db, 'config_rutinas'), orderBy('orden', 'asc'));
        const unsub = onSnapshot(q, snap => {
            const allRutinas = snap.docs.map(d => ({id: d.id, ...d.data()}));
            setRutinasAtencion(allRutinas.filter(r => r.departamento === 'Atención al Cliente'));
            setRutinasCuentas(allRutinas.filter(r => r.departamento === 'Cuentas'));
            setRutinasMarketing(allRutinas.filter(r => r.departamento === 'Marketing'));
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        const unsubLog = onSnapshot(doc(db, 'supervisor_logs', viewDate), (docSnap) => {
            if (docSnap.exists()) {
                setLogDia(docSnap.data().tasks || {});
            } else {
                setLogDia({});
            }
            setEvidenceInputs({}); // Reset inputs on date change or reload
        });
        return () => unsubLog();
    }, [viewDate]);

    const handleToggle = async (taskId, currentDone, currentEvidence) => {
        if (!isToday) return;
        const newLog = { ...logDia, [taskId]: { done: !currentDone, evidence: currentEvidence || '' } };
        await setDoc(doc(db, 'supervisor_logs', viewDate), { tasks: newLog }, { merge: true });
    };

    const handleEvidenceChange = (taskId, val) => {
        setEvidenceInputs(prev => ({ ...prev, [taskId]: val }));
    };

    const handleEvidenceBlur = async (taskId, currentDone) => {
        if (!isToday) return;
        const val = evidenceInputs[taskId] !== undefined ? evidenceInputs[taskId] : (logDia[taskId]?.evidence || '');
        if (logDia[taskId]?.evidence === val && val === '') return; // No change from empty

        const newLog = { ...logDia, [taskId]: { done: currentDone || false, evidence: val } };
        await setDoc(doc(db, 'supervisor_logs', viewDate), { tasks: newLog }, { merge: true });
    };

    const totalAtencion = rutinasAtencion.length;
    const doneAtencion = rutinasAtencion.filter(t => logDia[t.id]?.done).length;
    
    const totalCuentas = rutinasCuentas.length;
    const doneCuentas = rutinasCuentas.filter(t => logDia[t.id]?.done).length;

    const totalMarketing = rutinasMarketing.length;
    const doneMarketing = rutinasMarketing.filter(t => logDia[t.id]?.done).length;

    const totalTasks = activeTab === 'atencion' ? totalAtencion : activeTab === 'cuentas' ? totalCuentas : totalMarketing;
    const totalDone = activeTab === 'atencion' ? doneAtencion : activeTab === 'cuentas' ? doneCuentas : doneMarketing;
    const progress = totalTasks ? Math.round((totalDone / totalTasks) * 100) : 0;

    return (
        <div className="min-h-screen bg-slate-900">
            <PageHeader
                title="Supervisión"
                subtitle="Auditoría operativa y cumplimiento"
            />

            <div className="mt-6 grid gap-6 md:grid-cols-3">
                {/* SIDEBAR: DATE SELECTION & STATS */}
                <div className="md:col-span-1 space-y-6">
                    {/* Date Picker Card */}
                    <div className="bg-slate-800 p-5 rounded-xl border border-slate-700 shadow-lg">
                        <label className="block text-sm font-bold text-gray-400 mb-3 flex items-center gap-2">
                            <CalendarIcon size={16} /> Fecha de Registro
                        </label>
                        <input
                            type="date"
                            value={viewDate}
                            onChange={(e) => setViewDate(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-indigo-500 focus:outline-none"
                            max={new Date().toISOString().split('T')[0]} // Cannot predict future checks
                        />
                        {!isToday && (
                            <div className="mt-3 p-3 bg-amber-900/20 text-amber-400 text-xs rounded border border-amber-900/50 flex items-center gap-2">
                                <Clock size={14} />
                                Estás viendo un registro histórico (Solo Lectura)
                            </div>
                        )}
                    </div>

                    {/* Progress Card */}
                    <div className="bg-slate-800 p-5 rounded-xl border border-slate-700 shadow-lg flex flex-col items-center justify-center text-center">
                        <div className="relative size-32 mb-4">
                            <svg className="size-full" viewBox="0 0 36 36">
                                <path
                                    className="text-slate-700"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                                <path
                                    className="text-indigo-500 transition-all duration-1000 ease-out"
                                    strokeDasharray={`${progress}, 100`}
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center flex-col">
                                <span className="text-3xl font-bold text-white">{progress}%</span>
                                <span className="text-xs text-gray-400 uppercase">Completado</span>
                            </div>
                        </div>
                        <p className="text-gray-400 text-sm mt-4">
                            {totalDone} de {totalTasks} tareas revisadas
                        </p>
                    </div>
                </div>

                {/* MAIN CONTENT: CHECKLISTS */}
                <div className="md:col-span-2 space-y-6">
                    
                    {/* TABS */}
                    <div className="flex gap-2 border-b border-slate-700/50 pb-2">
                        {(!isCuentas && !isMarketing) && (
                            <button
                                onClick={() => setActiveTab('atencion')}
                                className={`px-4 py-2 font-bold rounded-t-lg transition-colors ${activeTab === 'atencion' ? 'bg-slate-800 text-blue-400 border-b-2 border-blue-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                Atención al Cliente
                            </button>
                        )}
                        {(!isAtencion && !isMarketing) && (
                            <button
                                onClick={() => setActiveTab('cuentas')}
                                className={`px-4 py-2 font-bold rounded-t-lg transition-colors ${activeTab === 'cuentas' ? 'bg-slate-800 text-emerald-400 border-b-2 border-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                Gestión de Cuentas
                            </button>
                        )}
                        {(!isAtencion && !isCuentas) && (
                            <button
                                onClick={() => setActiveTab('marketing')}
                                className={`px-4 py-2 font-bold rounded-t-lg transition-colors ${activeTab === 'marketing' ? 'bg-slate-800 text-amber-400 border-b-2 border-amber-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                Marketing
                            </button>
                        )}
                    </div>

                    {/* ATENCIÓN AL CLIENTE */}
                    {activeTab === 'atencion' && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden animate-in fade-in duration-300">
                        <div className="p-4 border-b border-slate-700 bg-slate-800/50 flex justify-between items-center">
                            <h3 className="font-bold flex items-center gap-2 text-blue-400">
                                <Users size={20} />
                                Atención al Cliente ({doneAtencion}/{totalAtencion})
                            </h3>
                            <span className="text-xs text-gray-500 bg-slate-900 px-2 py-1 rounded">
                                {isToday ? 'Modo Edición' : 'Modo Histórico'}
                            </span>
                        </div>

                        <div className="divide-y divide-slate-700">
                            {rutinasAtencion.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No hay rutinas configuradas.
                                </div>
                            ) : (
                                rutinasAtencion.map((task) => {
                                    const isDone = logDia[task.id]?.done || false;
                                    const evVal = evidenceInputs[task.id] !== undefined ? evidenceInputs[task.id] : (logDia[task.id]?.evidence || '');
                                    return (
                                    <div key={task.id} className={`p-3 sm:p-4 transition-colors ${isDone ? 'bg-indigo-900/10' : 'hover:bg-slate-700/30'}`}>
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            <button
                                                onClick={() => handleToggle(task.id, isDone, logDia[task.id]?.evidence)}
                                                disabled={!isToday}
                                                className={`mt-0.5 transition-colors ${isDone ? 'text-indigo-400' : 'text-gray-600 hover:text-gray-400'
                                                    } ${!isToday ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
                                            >
                                                {isDone ? <CheckSquare size={20} className="sm:w-6 sm:h-6" /> : <Square size={20} className="sm:w-6 sm:h-6" />}
                                            </button>

                                            <div className="flex-1 space-y-1.5">
                                                <div className={`font-medium text-base sm:text-lg leading-tight ${isDone ? 'text-gray-400 line-through' : 'text-gray-200'}`}>
                                                    {task.tarea}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder={isToday ? "Añadir nota o link de evidencia..." : "Sin evidencias"}
                                                        value={evVal}
                                                        disabled={!isToday}
                                                        onChange={(e) => handleEvidenceChange(task.id, e.target.value)}
                                                        onBlur={() => handleEvidenceBlur(task.id, isDone)}
                                                        className="w-full bg-slate-900/50 border border-slate-700 rounded px-2.5 py-1.5 text-xs sm:text-sm text-gray-300 placeholder-gray-600 focus:border-indigo-500 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    )}

                    {/* GESTIÓN DE CUENTAS */}
                    {activeTab === 'cuentas' && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden animate-in fade-in duration-300">
                        <div className="p-4 border-b border-slate-700 bg-slate-800/50 flex justify-between items-center">
                            <h3 className="font-bold flex items-center gap-2 text-emerald-400">
                                <ShieldCheck size={20} />
                                Gestión de Cuentas ({doneCuentas}/{totalCuentas})
                            </h3>
                            <span className="text-xs text-gray-500 bg-slate-900 px-2 py-1 rounded">
                                {isToday ? 'Modo Edición' : 'Modo Histórico'}
                            </span>
                        </div>

                        <div className="divide-y divide-slate-700">
                            {rutinasCuentas.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No hay rutinas configuradas.
                                </div>
                            ) : (
                                rutinasCuentas.map((task) => {
                                    const isDone = logDia[task.id]?.done || false;
                                    const evVal = evidenceInputs[task.id] !== undefined ? evidenceInputs[task.id] : (logDia[task.id]?.evidence || '');
                                    return (
                                    <div key={task.id} className={`p-3 sm:p-4 transition-colors ${isDone ? 'bg-indigo-900/10' : 'hover:bg-slate-700/30'}`}>
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            <button
                                                onClick={() => handleToggle(task.id, isDone, logDia[task.id]?.evidence)}
                                                disabled={!isToday}
                                                className={`mt-0.5 transition-colors ${isDone ? 'text-emerald-400' : 'text-gray-600 hover:text-gray-400'
                                                    } ${!isToday ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
                                            >
                                                {isDone ? <CheckSquare size={20} className="sm:w-6 sm:h-6" /> : <Square size={20} className="sm:w-6 sm:h-6" />}
                                            </button>

                                            <div className="flex-1 space-y-1.5">
                                                <div className={`font-medium text-base sm:text-lg leading-tight ${isDone ? 'text-gray-400 line-through' : 'text-gray-200'}`}>
                                                    {task.tarea}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder={isToday ? "Añadir nota o link de evidencia..." : "Sin evidencias"}
                                                        value={evVal}
                                                        disabled={!isToday}
                                                        onChange={(e) => handleEvidenceChange(task.id, e.target.value)}
                                                        onBlur={() => handleEvidenceBlur(task.id, isDone)}
                                                        className="w-full bg-slate-900/50 border border-emerald-900/30 rounded px-2.5 py-1.5 text-xs sm:text-sm text-gray-300 placeholder-gray-600 focus:border-emerald-500 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    )}

                    {/* MARKETING */}
                    {activeTab === 'marketing' && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden animate-in fade-in duration-300">
                        <div className="p-4 border-b border-slate-700 bg-slate-800/50 flex justify-between items-center">
                            <h3 className="font-bold flex items-center gap-2 text-amber-400">
                                <Users size={20} />
                                Marketing ({doneMarketing}/{totalMarketing})
                            </h3>
                            <span className="text-xs text-gray-500 bg-slate-900 px-2 py-1 rounded">
                                {isToday ? 'Modo Edición' : 'Modo Histórico'}
                            </span>
                        </div>

                        <div className="divide-y divide-slate-700">
                            {rutinasMarketing.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No hay rutinas configuradas.
                                </div>
                            ) : (
                                rutinasMarketing.map((task) => {
                                    const isDone = logDia[task.id]?.done || false;
                                    const evVal = evidenceInputs[task.id] !== undefined ? evidenceInputs[task.id] : (logDia[task.id]?.evidence || '');
                                    return (
                                    <div key={task.id} className={`p-3 sm:p-4 transition-colors ${isDone ? 'bg-indigo-900/10' : 'hover:bg-slate-700/30'}`}>
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            <button
                                                onClick={() => handleToggle(task.id, isDone, logDia[task.id]?.evidence)}
                                                disabled={!isToday}
                                                className={`mt-0.5 transition-colors ${isDone ? 'text-amber-400' : 'text-gray-600 hover:text-gray-400'
                                                    } ${!isToday ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
                                            >
                                                {isDone ? <CheckSquare size={20} className="sm:w-6 sm:h-6" /> : <Square size={20} className="sm:w-6 sm:h-6" />}
                                            </button>

                                            <div className="flex-1 space-y-1.5">
                                                <div className={`font-medium text-base sm:text-lg leading-tight ${isDone ? 'text-gray-400 line-through' : 'text-gray-200'}`}>
                                                    {task.tarea}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder={isToday ? "Añadir nota o link de evidencia..." : "Sin evidencias"}
                                                        value={evVal}
                                                        disabled={!isToday}
                                                        onChange={(e) => handleEvidenceChange(task.id, e.target.value)}
                                                        onBlur={() => handleEvidenceBlur(task.id, isDone)}
                                                        className="w-full bg-slate-900/50 border border-amber-500/50 rounded px-2.5 py-1.5 text-xs sm:text-sm text-gray-300 placeholder-gray-600 focus:border-amber-500 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupervisorLog;
